import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CASE_LABELS, CASE_ORDER } from "@/constants/content";
import { VerdictBanner } from "@/components/iv/scorecard/VerdictBanner";
import { GeoLensCards } from "@/components/iv/scorecard/GeoLensCards";
import { CriteriaMatrix } from "@/components/iv/scorecard/CriteriaMatrix";
import { TeardownSection } from "@/components/iv/scorecard/TeardownSection";
import { DiscoveryKit, ExecutionWindow } from "@/components/iv/scorecard/KitAndWindow";
import { NameSuggestions } from "@/components/iv/scorecard/NameSuggestions";
import { EvaluationProgress } from "@/components/iv/scorecard/EvaluationProgress";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Picker = ({ mine, value, onChange }) => {
  const { setActiveSlug } = useApp();
  // Keep the header case-study picker in sync so it never shows a stale case.
  const pick = (v) => { onChange(v); if (v.startsWith("case:")) setActiveSlug(v.slice(5)); };
  return (
  <div className="flex w-full min-w-0 items-center gap-2 text-xs text-slate-400 sm:w-auto">
    <span className="whitespace-nowrap">Report:</span>
    <Select value={value || undefined} onValueChange={pick}>
      <SelectTrigger className="h-9 w-full min-w-0 rounded-full border-white/10 bg-white/[0.04] text-sm text-white focus:ring-cyan-400/50 sm:w-[300px] [&>span]:truncate"
        data-testid="report-picker" aria-label="Report">
        <SelectValue placeholder="Choose a report" />
      </SelectTrigger>
      <SelectContent className="max-w-[calc(100vw-2rem)] border-white/10 bg-[#0B1021] text-white">
        {mine.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-[11px] text-slate-500">My evaluations</SelectLabel>
            {mine.map((e) => (
              <SelectItem key={e.id} value={e.id} data-testid={`report-option-${e.id}`}>
                {(e.idea_title || "Idea").slice(0, 34)} · {e.status === "completed" ? e.final_score?.toFixed(1) : e.status}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        <SelectGroup>
          <SelectLabel className="text-[11px] text-slate-500">Reference case studies</SelectLabel>
          {CASE_ORDER.map((slug) => <SelectItem key={slug} value={`case:${slug}`} data-testid={`report-option-${slug}`}>{CASE_LABELS[slug].label}</SelectItem>)}
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>
  );
};

export const ScorecardStage = ({ onStage }) => {
  const { user, authChecked, activeSlug, reports } = useApp();
  const [params] = useSearchParams();
  const [mine, setMine] = useState([]);
  const [selected, setSelected] = useState(params.get("evaluation"));
  const [ownReport, setOwnReport] = useState(null);
  const [progress, setProgress] = useState(null);
  const lastSlug = useRef(activeSlug);

  const loadMine = useCallback(() => {
    if (!user) return Promise.resolve([]);
    return api.get("/evaluations").then((r) => { setMine(r.data); return r.data; }).catch(() => []);
  }, [user]);

  useEffect(() => {
    if (!authChecked) return;
    loadMine().then((list) => setSelected((cur) => cur || (list[0]?.id ?? `case:${activeSlug}`)));
  }, [authChecked, loadMine]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { // header case switcher jumps to that reference report
    if (activeSlug !== lastSlug.current) { lastSlug.current = activeSlug; setSelected(`case:${activeSlug}`); }
  }, [activeSlug]);

  const isCase = !selected || selected.startsWith("case:");
  useEffect(() => {
    setOwnReport(null); setProgress(null);
    if (isCase || !selected) return undefined;
    let timer, alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/evaluations/${selected}/progress`);
        if (!alive) return;
        setProgress({ ...data, forId: selected });
        if (data.status === "completed") {
          const full = await api.get(`/evaluations/${selected}`);
          if (alive) { setOwnReport(full.data); loadMine(); }
        } else if (data.status !== "failed") timer = setTimeout(poll, 3000);
      } catch { if (alive) setProgress({ status: "failed", error: "Report not found or not yours.", forId: selected }); }
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [selected, isCase, loadMine]);

  // Never render data that belongs to a previous selection (stale-state guard).
  const report = isCase ? reports[selected?.slice(5) || activeSlug] : ownReport?.evaluation?.id === selected ? ownReport : null;
  const prog = !isCase && progress?.forId === selected ? progress : null;
  return (
    <section className="space-y-8 pb-12 pt-6" aria-labelledby="scorecard-h1" data-testid="scorecard-stage">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id="scorecard-h1" className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">360° Scorecard</h1>
          <p className="mt-2 text-sm text-slate-400">{isCase ? "Reference case study (demo content, indicative figures)." : "Your private evaluation — visible only to you."}</p>
        </div>
        <Picker mine={mine} value={selected} onChange={setSelected} />
      </div>
      {prog && prog.status !== "completed" && prog.status !== "failed" && <EvaluationProgress progress={prog} />}
      {!isCase && !prog && !report && <div className="h-40 animate-pulse rounded-2xl bg-white/[0.04]" data-testid="scorecard-skeleton" />}
      {prog?.status === "failed" && (
        <p className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="evaluation-failed">
          This evaluation couldn't complete{prog.error ? `: ${prog.error}` : ""}. Your free assessment was refunded — please run it again from Stage 02.
        </p>
      )}
      {report && (
        <div className="space-y-10" data-testid="scorecard-report">
          <VerdictBanner report={report} onStage={onStage} isOwner={!isCase} />
          <GeoLensCards geographies={report.geographies} />
          <CriteriaMatrix scores={report.criteria_scores} />
          <TeardownSection sections={report.bento_sections} />
          {report.discovery_script && <DiscoveryKit script={report.discovery_script} />}
          <ExecutionWindow report={report} onStage={onStage} isOwner={!isCase} />
          <NameSuggestions key={report.evaluation.id} report={report} isOwner={!isCase}
            onChosen={(name) => setOwnReport((r) => (r ? { ...r, idea: { ...r.idea, selected_brand_name: name } } : r))} />
        </div>
      )}
    </section>
  );
};
